import React, { useState, useEffect, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ReactComponent as CloseSvg } from "../svgs/close.svg";
import { ReactComponent as NoWifiSvg } from "../svgs/no_wifi.svg";
import { ReactComponent as SupportSvg } from "../svgs/support.svg";
import { ReactComponent as UserSvg } from "../svgs/user.svg";
import { useWarehouseContext } from "../WarehouseProvider";
import ExitModal from "./ExitModal";
import { LogIssueModal } from "./LogIssueModal";
import { useConnectionStatus } from "hooks/useConnectionStatus";
import { useSteps } from "../useSteps";
import AuthContext from "contexts/AuthContext";

export const Header = ({
  showModal = true,
  opacity = "100%",
  noExit = false,
  showLogIssueIcon = true,
  showUserIcon = false,
  children,
  onExit: customOnExit,
  isCameraActive = false,
}) => {
  const { clearTakeback } = useWarehouseContext();
  const { exit } = useSteps();
  const { user, logoutHandler } = useContext(AuthContext);

  const [showExitModal, setShowExitModal] = useState(false);
  const [showLogIssueModal, setShowLogIssueModal] = useState(false);
  const [showOfflineIcon, setShowOfflineIcon] = useState(false);
  const [isIntercomOpen, setIsIntercomOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const userDropdownRef = useRef(null);
  const userButtonRef = useRef(null);

  const onExit =
    customOnExit ||
    (() => {
      clearTakeback();
      exit({});
    });

  const isOnline = useConnectionStatus();

  useEffect(() => {
    let timer;
    if (!isOnline) {
      timer = setTimeout(() => {
        setShowOfflineIcon(true);
      }, 1000);
    } else {
      setShowOfflineIcon(false);
    }
    return () => clearTimeout(timer);
  }, [isOnline]);

  // Hide Intercom widget on mount
  useEffect(() => {
    const hideIntercom = () => {
      if (window.Intercom) {
        window.Intercom("hide");
        const container = document.querySelector(".intercom-lightweight-app");
        if (container) {
          setTimeout(() => {
            container.style.display = "none";
          }, 100);
        }
      }
    };

    // Check if Intercom is already loaded
    const intercomContainer = document.querySelector(
      ".intercom-lightweight-app"
    );
    if (intercomContainer) {
      hideIntercom();
    } else {
      // Wait for Intercom to load
      const observer = new MutationObserver((mutations, obs) => {
        const container = document.querySelector(".intercom-lightweight-app");
        if (container) {
          hideIntercom();
          obs.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }
  }, []);

  // Add global style to hide inactive intercom launcher
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `.intercom-launcher-frame { display: none !important; }`;
    document.head.appendChild(style);
  }, []);

  // Handle click outside for user dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target) &&
        userButtonRef.current &&
        !userButtonRef.current.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showUserDropdown]);

  return (
    <div
      className={`flex p-6 w-full min-h-[101px] justify-between items-center relative ${
        isCameraActive ? "bg-black/10 backdrop-blur-lg" : "bg-[#030711]"
      }`}
      style={{ opacity }}
    >
      {!noExit && (
        <button
          onClick={() =>
            showModal === true ? setShowExitModal(true) : onExit()
          }
          type="button"
          className="rounded-lg p-2 inline-flex items-center hover:bg-[#282F44]/20 transition-colors"
        >
          <CloseSvg className="w-6 h-6 fill-white" alt="Close" />
        </button>
      )}
      {showOfflineIcon && <NoWifiSvg />}
      {showExitModal &&
        createPortal(
          <AnimatePresence>
            <ExitModal
              isOpen={showExitModal}
              onClose={() => setShowExitModal(false)}
              onConfirm={onExit}
              title="Quit Processing Take Back"
              body="Are you sure you want to quit? All current processing for this take back will be lost."
              primaryText="Confirm"
              secondaryText="Back"
            />
          </AnimatePresence>,
          document.body
        )}

      {/* log error modal */}
      <div className="w-full">{children}</div>
      <div className="flex items-center gap-2">
        {showLogIssueIcon && (
          <button
            onClick={() => {
              const initContainer = document.querySelector(
                ".intercom-lightweight-app"
              );
              const container = document.querySelector(".intercom-app");
              if (!window.Intercom) {
                return;
              }
              if (
                !!initContainer ||
                (container && container.style.display === "none")
              ) {
                container && (container.style.display = "");
                setTimeout(() => {
                  window.Intercom("show");
                  setIsIntercomOpen(true);
                }, 100);
              } else if (container) {
                window.Intercom("hide");
                setTimeout(() => {
                  container.style.display = "none";
                  setIsIntercomOpen(false);
                }, 100);
              }
            }}
            type="button"
            className={`rounded-lg p-2 inline-flex items-center transition-colors ${isIntercomOpen ? "bg-[#C12960]" : "hover:bg-[#282F44]/20"}`}
          >
            <SupportSvg
              className={`w-6 h-6 ml-4 ${isIntercomOpen ? "fill-white" : isCameraActive ? "fill-white" : "fill-[#A1A7C2]"}`}
              alt="Support"
            />
          </button>
        )}
        {showUserIcon && user && (
          <div className="relative">
            <button
              ref={userButtonRef}
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              type="button"
              className="rounded-lg p-2 ml-4 inline-flex items-center hover:bg-[#282F44]/20 transition-colors"
            >
              <UserSvg
                className={`w-6 h-6 ${isIntercomOpen ? "fill-white" : isCameraActive ? "fill-white" : "fill-[#A1A7C2]"}`}
                alt="User"
              />
            </button>
            <AnimatePresence>
              {showUserDropdown && (
                <motion.div
                  ref={userDropdownRef}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 min-w-[16rem] max-w-max rounded-lg shadow-lg bg-[#0A1628] ring-1 ring-[#1F2A40] z-50"
                >
                  <div className="p-4 border-b border-[#1F2A40]">
                    <p className="text-sm font-semibold text-white">
                      {user.name}
                    </p>
                    <p className="text-sm text-[#8086A0] whitespace-nowrap">
                      {user.email}
                    </p>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowUserDropdown(false);
                        logoutHandler();
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[#1F2A40] rounded-md transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      {showLogIssueModal &&
        createPortal(
          <AnimatePresence>
            <LogIssueModal
              isOpen={showLogIssueModal}
              onClose={() => setShowLogIssueModal(false)}
            />
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};
